import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import axios from "axios"
import { fetchEnhancedUserInfo } from './masterSubAccountSlice'
import { unifiedLogin } from './authSlice'
import { logger } from '../../utils/logger'

// Define interfaces for the response data
interface Team {
  id: string;
  name: string;
}

interface Trap {
  trapId: string;
  trapName: string;
  location: string;
  building: string;
  lastestImageDate: string;
  probeModel: string;
  favorite: boolean;
  batteryInfo: string | null;
  wifiInfo: string | null;
  particleCount: number | null;
  originalFileUrl: string | null;
  timestamp: string | null;
  active: boolean | null;
  fileName: string | null;
  createdAt: string | null;
}

interface ScrollInfoResponse {
  trapLength: number;
  trapList: string[];
  traps: Trap[];
}

interface ScrollInfoPayload {
  teamName: string;
  filter: string;
  ascend: boolean;
  page?: number;
  pageSize?: number;
}

interface EmailNotiDays {
  Monday: boolean;
  Tuesday: boolean;
  Wednesday: boolean;
  Thursday: boolean;
  Friday: boolean;
  Saturday: boolean;
  Sunday: boolean;
}

// Session-related interfaces
interface ProbeSessionData {
  trapId: string;
  trapName?: string;
  activeSessions: string[];
}

interface ProbeDataList {
  probes: { [key: string]: ProbeSessionData };
}

interface SessionInsectCollection {
  moth: number;
  beetle: number;
  other: number;
}

interface SessionProbeData {
  sessionName: string;
  trapId: string;
  building: string;
  location: string;
  createdAt: string;
  originalFileUrl: string;
  particleCount: number;
  temperature: number;
  humidity: number;
  insectCollection: SessionInsectCollection;
  session: string;
}

interface Probe {
  probeId: string;
}



interface UserInfo {
  username: string;
  company: string;
  email: string;
  teams: string[];
  role: string;
  fahrenheit: boolean;
  yellow: number;
  lightRed: number;
  darkRed: number;
  filter: string;
  ascend: boolean;
  savedSSIDs: string[];
  notification: boolean;
  emailNotiDays: EmailNotiDays;
  emailList: string[];
  monthListSinceJoined: string[];
  probeListFormatted: {
    probes: Probe[];
  };
  probeList: string[];
}

interface ScrollInfoRequestPayload {
  msg: ScrollInfoPayload;
  num: number;
}

interface UpdateTrapPayload {
  id: string;
  trapName: string;
  location: string;
  building: string;
  frequency: string;
  brightness: string;
  minutes: string;
  hours: string;
  days: string;
  pwm: string;
}

interface DeleteTrapPushPayload {
  trapId: string;
  fileName: string;
  teamName: string;
}

interface RemoveTrapPayload {
  trapId: string;
  teamName: string;
}

interface CleanTrapPayload {
  trapId: string;
  teamName: string;
  sessionName: string;
}

interface DeleteCurrentDataPayload {
  trapId: string;
  teamName: string;
}

interface DeleteAllDataPayload {
  trapId: string;
  teamName: string;
}

// Add async report interfaces
interface AsyncJobStatus {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  message: string;
  createdAt: string;
  updatedAt: string;
  error?: string;
}

interface StartAsyncReportPayload {
  month: string;
  probes: { probeId: string }[];
  timeZone?: string;
}

const DataView = {
  trapId: -1,
  dataLst: null,
  traps: null,
  trapLength: null,
  frequency: -1,
  brightness: -1,
  location: null,
  building: null,
  trapName: null,
  probeList: null,
  regKeyList: null,
  traplistLoading: true,
  trapDataLoading: false,
  regKeyMessage: null,
  smartProbeOrders: null,
  specificSmartProbeOrder: [],
  orderModalHelper: true,
  scrollInfo: null as ScrollInfoResponse | null,
  userInfo: null as UserInfo | null,
  currentTeam: null as Team | null,
  // Add async report state
  asyncReport: {
    jobId: null as string | null,
    status: null as AsyncJobStatus | null,
    isGenerating: false,
    error: null as string | null,
  },
  // Add teams data export state
  teamsExport: {
    jobId: null as string | null,
    status: null as AsyncJobStatus | null,
    isGenerating: false,
    error: null as string | null,
  },
  // Track which job type is currently active
  activeJobType: null as 'monthly' | 'teams' | null,
  // Session-related state
  probeDataList: null as ProbeDataList | null,
  sessionProbeData: null as SessionProbeData[] | null,
  sessionFlag: false
}

export const getScrollInfo = createAsyncThunk("data/scrollInfo", async (payload: ScrollInfoRequestPayload) => {
  const body = payload.msg
  const response = await axios.put("/api/data/scrollinfo/probes/" + payload.num, body, {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
    },
  })
  return response.data
})

export const fetchTrapInfo = createAsyncThunk("data/trapInfo", async (id: string) => {
  const response = await axios.get("/api/traps/" + id, {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
    },
  })
  //console.log(response.data);
  response.data.trap.id = id
  return response.data
})

export const fetchDataLst = createAsyncThunk("data/dataLst", async (id: string) => {
  const response = await axios.get("/api/data/trapProbe/" + id, {
    headers: {
      Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
    },
  })

  return response.data
})

export const resetDataLst = createAsyncThunk("data/resetDataLst", async () => {
  return true
})

export const getScrollInfoV2 = createAsyncThunk(
  "data/scrollInfoV2", 
  async (payload: ScrollInfoPayload) => {
    const response = await axios.put(
      "/api/data/scrollinfo/probesV2", 
      payload, 
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        },
      }
    );
    return response.data;
  }
);

// New thunk with filter and pagination support (like mobile app)
export const fetchScrollInfo = createAsyncThunk(
  "data/fetchScrollInfo", 
  async (payload: ScrollInfoRequestPayload) => {
    const response = await axios.put(
      "/api/data/scrollinfo/probes", 
      payload, 
      {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        },
      }
    );
    return response.data;
  }
);

export const fetchUserInfo = createAsyncThunk(
  "data/fetchUserInfo", 
  async () => {
    const response = await axios.get("/api/users", {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    return response.data;
  }
);

export const updateTrap = createAsyncThunk(
  "data/updateTrap",
  async (payload: UpdateTrapPayload) => {
    const token = await localStorage.getItem("GrainGuardianToken");
    const body = {
      trap: {
        trapName: payload.trapName,
        location: payload.location,
        building: payload.building,
        frequency: payload.frequency,
        brightness: payload.brightness,
        minutes: payload.minutes,
        hours: payload.hours,
        days: payload.days,
        pwm: payload.pwm,
      },
    };

    const response = await axios.put("/api/traps/update/" + payload.id, body, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });

    return response.data;
  }
);

export const changeInsectCount = createAsyncThunk(
  "data/changeInsectCount",
  async (msg: { fileName: string; trapId: string; newCount: number; insect: string }) => {
    const response = await axios.post("/api/insect/changeInsectCount", msg, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    return response.data;
  }
);

export const deleteTrapPush = createAsyncThunk(
  "data/deleteTrapPush",
  async (payload: DeleteTrapPushPayload) => {
    const body = {
      trapId: payload.trapId.toString(),
      fileName: payload.fileName,
      teamName: payload.teamName,
    };
    const response = await axios.delete("/api/data/trapTeams", {
      data: body,
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    return response.data;
  }
);

export const removeTrap = createAsyncThunk(
  "data/removeTrap",
  async (payload: RemoveTrapPayload) => {
    const token = await localStorage.getItem("GrainGuardianToken");
    const body = {
      trapId: payload.trapId.toString(),
      teamName: payload.teamName,
    };
    const response = await axios.post("/api/data/trapTeams/session/remove", body, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
    return response.data;
  }
);

export const cleanTrap = createAsyncThunk(
  "data/cleanTrap",
  async (payload: CleanTrapPayload) => {
    const token = await localStorage.getItem("GrainGuardianToken");
    const response = await axios.post(
      "/api/probefunction/trapTeams/probeSession/all",
      payload,
      {
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    );
    return response.data;
  }
);

export const deleteCurrentData = createAsyncThunk(
  "data/deleteCurrentData",
  async (payload: DeleteCurrentDataPayload) => {
    const token = await localStorage.getItem("GrainGuardianToken");
    const response = await axios.delete(
      "/api/probefunction/trap/probeCurrent/delete",
      {
        data: payload,
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    );
    return response.data;
  }
);

export const deleteAllData = createAsyncThunk(
  "data/deleteAllData",
  async (payload: DeleteAllDataPayload) => {
    const token = await localStorage.getItem("GrainGuardianToken");
    const body = {
      trapId: payload.trapId.toString(),
      teamName: payload.teamName,
    };
    const response = await axios.delete(
      "/api/probefunction/trapTeams/probeAll/delete",
      {
        data: body,
        headers: {
          Authorization: "Bearer " + token,
        },
      }
    );
    return response.data;
  }
);

// Additional thunks for account management
export const updateAccount = createAsyncThunk(
  "data/updateAccount",
  async (payload: { company: string }) => {
    const body = {
      user: {
        company: payload.company,
      },
    };
    const response = await axios.put("/api/users/update", body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    return response.data;
  }
);

export const toggleTempUnit = createAsyncThunk(
  "data/toggleTempUnit",
  async () => {
    const body = {};
    const response = await axios.put("/api/users/fahrenheit", body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    return response.data;
  }
);

export const changeInsectCountThreshold = createAsyncThunk(
  "data/changeInsectCountThreshold",
  async (payload: { yellow: number; lightRed: number; darkRed: number }) => {
    const body = payload;
    const token = localStorage.getItem("GrainGuardianToken");
    const response = await axios.put("/api/users/changeIndicators", body, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
    return response.data;
  }
);

export const toggleEmailNoti = createAsyncThunk(
  "data/toggleEmailNoti",
  async (_, { rejectWithValue }) => {
    const body = {};
    const token = localStorage.getItem("GrainGuardianToken");
    try {
      const response = await axios.put("/api/notification", body, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      logger.log("LOG: Email Notification Toggle Ran");
      return response.data;
    } catch (err) {
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Email notification toggle failed');
    }
  }
);

export const toggleEmailNotiDay = createAsyncThunk(
  "data/toggleEmailNotiDay",
  async (payload: { day: string }, { rejectWithValue }) => {
    const body = payload;
    const token = localStorage.getItem("GrainGuardianToken");
    try {
      const response = await axios.put("/api/notification/days", body, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      logger.log("LOG: Email Notification Day Toggle Ran");
      return response.data;
    } catch (err) {
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Email notification day toggle failed');
    }
  }
);

export const addEmail = createAsyncThunk(
  "data/addEmail",
  async (payload: { email: string }, { rejectWithValue }) => {
    const body = payload;
    const token = localStorage.getItem("GrainGuardianToken");
    try {
      const response = await axios.put("/api/notification/addEmail", body, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      logger.log("LOG: Add Email to Account Ran");
      return response.data;
    } catch (err) {
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Add email failed');
    }
  }
);

export const removeEmail = createAsyncThunk(
  "data/removeEmail",
  async (payload: { email: string }, { rejectWithValue }) => {
    const body = payload;
    const token = localStorage.getItem("GrainGuardianToken");
    try {
      const response = await axios.put("/api/notification/removeEmail", body, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });
      logger.log("LOG: Remove Email from Account Ran");
      return response.data;
    } catch (err) {
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Remove email failed');
    }
  }
);

export const changePassword = createAsyncThunk(
  "data/changePassword",
  async (payload: { email: string; password: string; newpassword: string }, { rejectWithValue }) => {
    const body = { user: payload };
    try {
      const { AUTH_ENDPOINTS } = await import("../../config/apiEndpoints");
      const response = await axios.put(AUTH_ENDPOINTS.CHANGE_PASSWORD, body, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        },
      });
      return response.data;
    } catch (err) {
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Password change failed');
    }
  }
);



export const downloadAllData = createAsyncThunk(
  "data/downloadAllData", 
  async (payload: { probes: (string | { probeId: string })[] }) => {
    const body = {
      probes: payload.probes,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    const response = await axios.post("/api/dataDownload/exportdataTeams", body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
      responseType: 'blob',
    });
    
    // Create download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'SmartProbe.xlsx');
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return response.data;
  }
);

export const downloadMonthlyData = createAsyncThunk(
  "data/downloadMonthlyData", 
  async (payload: { probes: (string | { probeId: string })[]; month: string }) => {
    const body = {
      probes: payload.probes,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      month: payload.month
    };
    
    const response = await axios.post("/api/dataDownload/downloadMonthlyReport", body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
      responseType: 'blob',
    });
    logger.log('Monthly download response: COMPLETED');
    // Create download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `SmartProbe ${payload.month}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    
    return response.data;
  }
);

export const allProbeDataFromAccount = createAsyncThunk(
  "data/allProbeDataFromAccount",
  async (payload: { teamName: string }, { rejectWithValue }) => {
    try {
      const body = payload;
      const response = await axios.post("/api/session/allProbeDataFromAccount", body, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        },
      });
      return response.data;
    } catch (err) {
      logger.error("allProbeDataFromAccount - error:", err);
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Failed to fetch probe data');
    }
  }
);

export const getInsectBySessionAndTrap = createAsyncThunk(
  "data/getInsectBySessionAndTrap",
  async (payload: { sessionName: string; trapId: string }, { rejectWithValue }) => {
    try {
      const body = payload;
      const response = await axios.post("/api/session/getInsectsBySessionAndTrap", body, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        },
      });
      return response.data;
    } catch (err) {
      logger.error("getInsectBySessionAndTrap - error:", err);
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Failed to fetch session data');
    }
  }
);

export const moveInsectsToDeleted = createAsyncThunk(
  "data/moveInsectsToDeleted",
  async (payload: { sessionName: string; trapId: string }, { rejectWithValue }) => {
    try {
      const body = payload;
      const response = await axios.post("/api/session/moveInsectsToDeleted", body, {
        headers: {
          Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        },
      });
      return response.data;
    } catch (err) {
      logger.error("moveInsectsToDeleted - error:", err);
      const error = err as { response?: { data?: string } };
      return rejectWithValue(error.response?.data || 'Failed to delete session');
    }
  }
);

// Async Data Download Thunks
export const startAsyncMonthlyReport = createAsyncThunk(
  "data/startAsyncMonthlyReport",
  async (payload: StartAsyncReportPayload) => {
    const response = await axios.post("/api/asyncDataDownload/startMonthlyReport", payload, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
        "Content-Type": "application/json",
      },
    });
    return response.data;
  }
);

export const checkAsyncJobStatus = createAsyncThunk(
  "data/checkAsyncJobStatus",
  async (jobId: string) => {
    const response = await axios.get(`/api/asyncDataDownload/jobStatus/${jobId}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    return response.data;
  }
);

export const startTeamsDataExport = createAsyncThunk(
  "data/startTeamsDataExport",
  async (payload: { teamName: string; timeZone?: string }) => {
    const body = {
      teamName: payload.teamName,
      timeZone: payload.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone
    };
    
    const response = await axios.post("/api/asyncDataDownload/startTeamsDataExport", body, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
    });
    
    return response.data;
  }
);

export const downloadAsyncReport = createAsyncThunk(
  "data/downloadAsyncReport",
  async (jobId: string) => {
    const response = await axios.get(`/api/asyncDataDownload/download/${jobId}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("GrainGuardianToken"),
      },
      responseType: 'blob',
    });
    
    // Create download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `SmartProbeReport_${jobId}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return response.data;
  }
);

export const dataSlice = createSlice({
  name: "data",
  initialState: DataView,
  reducers: {
    setCurrentTeam: (state, action) => {
      state.currentTeam = action.payload;
    },
    // Add async report actions
    resetAsyncReport: (state) => {
      state.asyncReport = {
        jobId: null,
        status: null,
        isGenerating: false,
        error: null,
      };
      if (state.activeJobType === 'monthly') {
        state.activeJobType = null;
      }
    },
    setAsyncReportError: (state, action) => {
      state.asyncReport.error = action.payload;
      state.asyncReport.isGenerating = false;
    },
    resetTeamsExport: (state) => {
      state.teamsExport = {
        jobId: null,
        status: null,
        isGenerating: false,
        error: null,
      };
      if (state.activeJobType === 'teams') {
        state.activeJobType = null;
      }
    },
    setTeamsExportError: (state, action) => {
      state.teamsExport.error = action.payload;
      state.teamsExport.isGenerating = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getScrollInfo.pending, () => {
      // No need to use state or action parameters if not needed
    })
    builder.addCase(fetchDataLst.pending, (state) => {
      state.trapDataLoading = false
      //console.log("datalist pending");
    })
    builder.addCase(getScrollInfo.fulfilled, (state, action) => {
      //console.log("scrollinfo fulfilled", action.payload.trapList)
      state.trapId = action.payload.trapList[0]
      state.traps = action.payload.traps
      state.trapLength = action.payload.trapLength
      state.traplistLoading = true
    })
    builder.addCase(fetchTrapInfo.fulfilled, (state, action) => {
      state.frequency = Number.parseInt(action.payload.trap.frequency)
      state.brightness = Number.parseInt(action.payload.trap.brightness)
      state.location = action.payload.trap.location
      state.building = action.payload.trap.building
      state.trapName = action.payload.trap.trapName
      state.trapId = Number.parseInt(action.payload.trap.id)
    })
    builder.addCase(fetchDataLst.fulfilled, (state, action) => {
      state.dataLst = action.payload.trap.dataLst
      state.trapDataLoading = true
    })
    builder.addCase(getScrollInfoV2.pending, (state) => {
      state.traplistLoading = false;
    });
    
    builder.addCase(getScrollInfoV2.fulfilled, (state, action) => {
      state.scrollInfo = action.payload;
      state.traplistLoading = true;
    });
    
    builder.addCase(fetchUserInfo.pending, () => {
      // You can add loading state if needed
    });
    
    builder.addCase(fetchUserInfo.fulfilled, (state, action) => {
      state.userInfo = action.payload.userInfo;
      // Set the first team as current team ONLY if no team is currently selected
      if (!state.currentTeam && action.payload.userInfo.teams && action.payload.userInfo.teams.length > 0) {
        state.currentTeam = {
          id: action.payload.userInfo.teams[0],
          name: action.payload.userInfo.teams[0]
        };
      }
    });
    
    // Listen to unifiedLogin from authSlice to set initial team for sub-accounts
    builder.addCase(unifiedLogin.fulfilled, (state, action) => {
      logger.log('🎯 dataSlice: Received unifiedLogin.fulfilled');
      logger.log('🎯 dataSlice: Login payload:', JSON.stringify(action.payload, null, 2));
      
      // Set currentTeam from login response teams (especially important for sub-accounts)
      if (action.payload.user.teams && action.payload.user.teams.length > 0 && !state.currentTeam) {
        const firstTeam = action.payload.user.teams[0];
        const teamToSet = {
          id: firstTeam,
          name: firstTeam
        };
        logger.log('🎯 dataSlice: Setting team from login:', teamToSet);
        state.currentTeam = teamToSet;
      }
    });
    
    // Listen to fetchEnhancedUserInfo from masterSubAccountSlice to set master team
    builder.addCase(fetchEnhancedUserInfo.fulfilled, (state, action) => {
      logger.log('🎯 dataSlice: Received fetchEnhancedUserInfo.fulfilled');
      logger.log('🎯 dataSlice: Payload:', JSON.stringify(action.payload, null, 2));
      // If master account, always set/update master team from masterAccountInfo
      if (action.payload.isMaster && action.payload.masterAccountInfo) {
        // Extract team name from teamName array (API provides teamName, not teams)
        let masterTeamName: string | undefined;
        const masterAccount = action.payload.masterAccountInfo.masterAccount;
        
        if (Array.isArray(masterAccount.teamName) && masterAccount.teamName.length > 0) {
          masterTeamName = masterAccount.teamName[0];
        } else if (typeof masterAccount.teamName === 'string' && masterAccount.teamName) {
          masterTeamName = masterAccount.teamName;
        } else if (Array.isArray((masterAccount as any).teams) && (masterAccount as any).teams.length > 0) {
          // Fallback to teams field if teamName doesn't exist
          masterTeamName = (masterAccount as any).teams[0];
        } else if (typeof (masterAccount as any).teams === 'string') {
          masterTeamName = (masterAccount as any).teams;
        }
        
        if (masterTeamName) {
          const teamToSet = {
            id: masterAccount.uid || 'master',
            name: masterTeamName
          };
          logger.log('🎯 dataSlice: Setting master team object:', teamToSet);
          logger.log('🎯 dataSlice: Team name extracted from teamName:', masterTeamName);
          state.currentTeam = teamToSet;
          logger.log('🎯 dataSlice: currentTeam after setting:', state.currentTeam);
        } else {
          logger.warn('⚠️ dataSlice: Could not extract master team name from:', masterAccount);
        }
      }
    });
    
    builder.addCase(resetDataLst.fulfilled, (state) => {
      state.trapId = -1
      state.dataLst = null
      state.traps = null
      state.trapLength = null
      state.traplistLoading = true
      state.frequency = -1
      state.brightness = -1
      state.location = null
      state.building = null
      state.trapName = null
    })

    builder.addCase(updateTrap.pending, (state) => {
      state.traplistLoading = false;
    });

    builder.addCase(updateTrap.fulfilled, (state, action) => {
      // Update the trap in the scrollInfo if it exists
      if (state.scrollInfo?.traps) {
        const updatedTraps = state.scrollInfo.traps.map(trap => 
          trap.trapId === action.payload.trap.id 
            ? { 
                ...trap, 
                trapName: action.payload.trap.trapName,
                location: action.payload.trap.location,
                building: action.payload.trap.building,
                frequency: action.payload.trap.frequency,
                brightness: action.payload.trap.brightness
              }
            : trap
        );
        state.scrollInfo = {
          ...state.scrollInfo,
          traps: updatedTraps
        };
      }
      state.traplistLoading = true;
    });

    builder.addCase(updateTrap.rejected, (state) => {
      state.traplistLoading = true;
    });

    builder
      .addCase(changeInsectCount.pending, (state) => {
        state.traplistLoading = true;
      })
      .addCase(changeInsectCount.fulfilled, (state) => {
        state.traplistLoading = false;
      })
      .addCase(changeInsectCount.rejected, (state) => {
        state.traplistLoading = false;
      });

    builder
      .addCase(deleteTrapPush.pending, (state) => {
        state.traplistLoading = true;
      })
      .addCase(deleteTrapPush.fulfilled, (state) => {
        state.traplistLoading = false;
      })
      .addCase(deleteTrapPush.rejected, (state) => {
        state.traplistLoading = false;
      });

    builder
      .addCase(removeTrap.pending, (state) => {
        state.traplistLoading = true;
      })
      .addCase(removeTrap.fulfilled, (state) => {
        state.traplistLoading = false;
      })
      .addCase(removeTrap.rejected, (state) => {
        state.traplistLoading = false;
      })
      .addCase(cleanTrap.pending, (state) => {
        state.traplistLoading = true;
      })
      .addCase(cleanTrap.fulfilled, (state) => {
        state.traplistLoading = false;
      })
      .addCase(cleanTrap.rejected, (state) => {
        state.traplistLoading = false;
      })
      .addCase(deleteCurrentData.pending, (state) => {
        state.traplistLoading = true;
      })
      .addCase(deleteCurrentData.fulfilled, (state) => {
        state.traplistLoading = false;
      })
      .addCase(deleteCurrentData.rejected, (state) => {
        state.traplistLoading = false;
      })
      .addCase(deleteAllData.pending, (state) => {
        state.traplistLoading = true;
      })
      .addCase(deleteAllData.fulfilled, (state) => {
        state.traplistLoading = false;
      })
      .addCase(deleteAllData.rejected, (state) => {
        state.traplistLoading = false;
      });

    // Add async report cases
    builder
      .addCase(startAsyncMonthlyReport.pending, (state) => {
        state.asyncReport.isGenerating = true;
        state.asyncReport.error = null;
        state.activeJobType = 'monthly';
      })
      .addCase(startAsyncMonthlyReport.fulfilled, (state, action) => {
        state.asyncReport.jobId = action.payload.jobId;
        state.asyncReport.status = action.payload;
      })
      .addCase(startAsyncMonthlyReport.rejected, (state, action) => {
        state.asyncReport.isGenerating = false;
        state.asyncReport.error = action.error.message || 'Failed to start report generation';
        state.activeJobType = null;
      })
      .addCase(checkAsyncJobStatus.fulfilled, (state, action) => {
        // Update the correct state based on active job type
        if (state.activeJobType === 'monthly') {
          state.asyncReport.status = action.payload;
          if (action.payload.status === 'completed' || action.payload.status === 'failed') {
            state.asyncReport.isGenerating = false;
            if (action.payload.status === 'failed') {
              state.activeJobType = null;
            }
          }
        } else if (state.activeJobType === 'teams') {
          state.teamsExport.status = action.payload;
          if (action.payload.status === 'completed' || action.payload.status === 'failed') {
            state.teamsExport.isGenerating = false;
            if (action.payload.status === 'failed') {
              state.activeJobType = null;
            }
          }
        }
      })
      .addCase(checkAsyncJobStatus.rejected, (state, action) => {
        state.asyncReport.error = action.error.message || 'Failed to check job status';
      })
      .addCase(downloadAsyncReport.fulfilled, (state) => {
        // Reset the correct state after successful download
        if (state.activeJobType === 'monthly') {
          state.asyncReport = {
            jobId: null,
            status: null,
            isGenerating: false,
            error: null,
          };
        } else if (state.activeJobType === 'teams') {
          state.teamsExport = {
            jobId: null,
            status: null,
            isGenerating: false,
            error: null,
          };
        }
        state.activeJobType = null;
      })
      // Teams export reducers
      .addCase(startTeamsDataExport.pending, (state) => {
        state.teamsExport.isGenerating = true;
        state.teamsExport.error = null;
        state.activeJobType = 'teams';
      })
      .addCase(startTeamsDataExport.fulfilled, (state, action) => {
        state.teamsExport.jobId = action.payload.jobId;
        state.teamsExport.status = action.payload;
      })
      .addCase(startTeamsDataExport.rejected, (state, action) => {
        state.teamsExport.isGenerating = false;
        state.teamsExport.error = action.error.message || 'Failed to start teams data export';
        state.activeJobType = null;
      })
      .addCase(downloadAsyncReport.rejected, (state, action) => {
        state.asyncReport.error = action.error.message || 'Download failed';
      })
      
      // Email notification reducers
      .addCase(toggleEmailNoti.fulfilled, (state) => {
        // Update userInfo state after successful toggle
        if (state.userInfo) {
          state.userInfo.notification = !state.userInfo.notification;
        }
      })
      .addCase(toggleEmailNotiDay.fulfilled, (state, action) => {
        // Update userInfo emailNotiDays after successful toggle
        if (state.userInfo && action.meta.arg && action.meta.arg.day) {
          const dayKey = action.meta.arg.day as keyof typeof state.userInfo.emailNotiDays;
          if (state.userInfo.emailNotiDays && dayKey) {
            // Toggle the day value
            state.userInfo.emailNotiDays[dayKey] = !state.userInfo.emailNotiDays[dayKey];
          }
        }
      })
      .addCase(addEmail.fulfilled, (state, action) => {
        // Add email to userInfo emailList after successful add
        if (state.userInfo && action.meta.arg.email) {
          if (!state.userInfo.emailList.includes(action.meta.arg.email)) {
            state.userInfo.emailList.push(action.meta.arg.email);
          }
        }
      })
      .addCase(removeEmail.fulfilled, (state, action) => {
        // Remove email from userInfo emailList after successful removal
        if (state.userInfo && action.meta.arg.email) {
          state.userInfo.emailList = state.userInfo.emailList.filter(
            email => email !== action.meta.arg.email
          );
        }
      })
      
      // Session reducers
      .addCase(allProbeDataFromAccount.fulfilled, (state, action) => {
        state.probeDataList = action.payload;
      })
      .addCase(allProbeDataFromAccount.rejected, (state, action) => {
        logger.error("Redux: allProbeDataFromAccount.rejected with:", action.error);
        state.probeDataList = null;
      })
      .addCase(getInsectBySessionAndTrap.fulfilled, (state, action) => {
        state.sessionProbeData = action.payload;
      })
      .addCase(getInsectBySessionAndTrap.rejected, (state, action) => {
        logger.error("Redux: getInsectBySessionAndTrap.rejected with:", action.error);
        state.sessionProbeData = null;
      })
      .addCase(moveInsectsToDeleted.fulfilled, (state) => {
        logger.log("Redux: moveInsectsToDeleted.fulfilled");
        state.sessionFlag = !state.sessionFlag;
        state.sessionProbeData = null;
      })
      .addCase(moveInsectsToDeleted.rejected, (state, action) => {
        logger.error("Redux: moveInsectsToDeleted.rejected with:", action.error);
      });
  },
})

// Selectors
export const selectCurrentTeam = (state: { data: typeof DataView }) => state.data.currentTeam;
export const selectAsyncReportState = (state: { data: typeof DataView }) => state.data.asyncReport;
export const selectTeamsExportState = (state: { data: typeof DataView }) => state.data.teamsExport;

export const { setCurrentTeam, resetAsyncReport, setAsyncReportError, resetTeamsExport, setTeamsExportError } = dataSlice.actions;
export default dataSlice.reducer
